import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { 
  Box, 
  Typography, 
  Chip, 
  Card,
  CardContent,
  IconButton
} from '@mui/joy';
import { Plus, TrendUp as TrendingUp, CurrencyDollar as DollarSign } from '@phosphor-icons/react';
import type { Opportunity, OpportunityBoardStatus } from '../../types';
import { OpportunityCard } from '../crm/OpportunityCard';

interface OpportunityKanbanColumnProps {
  status: OpportunityBoardStatus;
  opportunities: Opportunity[];
  onAddOpportunity?: (statusId: number) => void;
  onEditOpportunity?: (opportunity: Opportunity) => void;
  onDeleteOpportunity?: (opportunityId: number) => void;
}

export const OpportunityKanbanColumn: React.FC<OpportunityKanbanColumnProps> = ({
  status,
  opportunities,
  onAddOpportunity,
  onEditOpportunity,
  onDeleteOpportunity,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status.id}`,
  });

  // Ensure opportunities is an array
  const safeOpportunities = Array.isArray(opportunities) ? opportunities : [];
  
  // Log for debugging
  console.log(`[OpportunityKanbanColumn] Status ${status.name}:`, {
    opportunitiesType: Array.isArray(opportunities) ? 'array' : typeof opportunities,
    count: safeOpportunities.length
  });

  // Calculate column metrics
  const totalValue = safeOpportunities.reduce((sum, opp) => {
    const value = parseFloat(String(opp.estimated_value || 0));
    return sum + (isNaN(value) ? 0 : value);
  }, 0);
  const weightedValue = safeOpportunities.reduce((sum, opp) => {
    const value = parseFloat(String(opp.estimated_value || 0));
    const numValue = isNaN(value) ? 0 : value;
    return sum + (numValue * opp.probability / 100);
  }, 0);
  const avgProbability = safeOpportunities.length > 0 
    ? Math.round(safeOpportunities.reduce((sum, opp) => sum + opp.probability, 0) / safeOpportunities.length)
    : 0;

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    } else {
      return `$${value.toFixed(0)}`;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Column Header */}
      <Card variant="outlined" sx={{ mb: 1, flexShrink: 0, bgcolor: 'background.level1' }}>
        <CardContent sx={{ p: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="title-sm" fontWeight="lg">
                {status.name}
              </Typography>
              <Chip size="sm" variant="soft" color="neutral">
                {safeOpportunities.length}
              </Chip>
            </Box>
            {onAddOpportunity && (
              <IconButton
                size="sm"
                variant="plain"
                color="primary"
                onClick={() => onAddOpportunity(status.id)}
              >
                <Plus size={14} />
              </IconButton>
            )}
          </Box>

          {/* Column Metrics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justify: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DollarSign size={12} />
                <Typography level="body-xs" textColor="text.secondary">
                  Total: {formatCurrency(totalValue)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUp size={12} />
                <Typography level="body-xs" textColor="text.secondary">
                  {avgProbability}%
                </Typography>
              </Box>
            </Box>
            
            {safeOpportunities.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography level="body-xs" textColor="success.600" fontWeight="lg">
                  Weighted: {formatCurrency(weightedValue)}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Drop Zone */}
      <Box
        ref={setNodeRef}
        className="kanban-column"
        sx={{
          flex: 1,
          minHeight: '500px', // Ensure minimum height for drop zone
          bgcolor: 'background.surface',
          borderRadius: 'sm',
          p: 1,
          border: isOver ? '2px dashed' : '1px solid',
          borderColor: isOver ? 'primary.300' : 'neutral.200',
          transition: 'all 0.2s ease',
          overflowY: 'auto', // Enable vertical scrolling within the column
          overflowX: 'hidden',
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'var(--joy-palette-background-level1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--joy-palette-neutral-400)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'var(--joy-palette-neutral-500)',
            },
          },
        }}
      >
        <SortableContext 
          items={safeOpportunities.map(opp => opp.id)} 
          strategy={verticalListSortingStrategy}
        >
          {safeOpportunities.map((opportunity) => (
            <OpportunityCard
              key={opportunity.id}
              opportunity={opportunity}
              onEdit={onEditOpportunity}
              onDelete={onDeleteOpportunity}
            />
          ))}
        </SortableContext>

        {safeOpportunities.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 4,
            gap: 2
          }}>
            <Typography level="body-sm" textColor="text.secondary">
              No opportunities in this status
            </Typography>
            {onAddOpportunity && (
              <IconButton
                variant="soft"
                color="primary"
                onClick={() => onAddOpportunity(status.id)}
              >
                <Plus size={16} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};